import { Component, Input } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { MessageComponent } from 'src/app/@shared/components/message/message.component';

// Modèles
import { AssistantAuthorizationRequest } from 'src/app/@shared/models/interfaces/assistant-authorization-request.model';
import { Client } from 'src/app/@shared/models/client/client.model';
import { AssistantAuthorizationResult } from 'src/app/@core/services/interfaces/assistant-authorization.service';

/**
 * Décision de l'utilisateur sur la demande d'un assistant IA (#1312) : le choix d'un
 * seul dossier, puis Autoriser ou Refuser. Les deux issues renvoient l'utilisateur
 * chez l'assistant — avec le code d'autorisation, ou avec `error=access_denied`.
 */
@Component({
    selector: 'app-assistant-authorization-form',
    imports: [
        ReactiveFormsModule,
        ButtonModule,
        SelectModule,
        TagModule,
        MessageComponent
    ],
    templateUrl: './assistant-authorization-form.component.html',
    styleUrls: ['./assistant-authorization-form.component.scss']
})
export class AssistantAuthorizationFormComponent extends FormBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    public readonly grantedScope: string[] = [
        'Il agit avec vos droits, sur ce dossier seulement.',
        'Ce qu\'il écrit est enregistré à votre nom.',
        'Vous pouvez lui retirer cet accès à tout moment depuis Paramètres → Interfaces → Assistants connectés.'
    ];

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _authorizationRequest!: AssistantAuthorizationRequest;
    public get authorizationRequest(): AssistantAuthorizationRequest {
        return this._authorizationRequest;
    }
    @Input()
    public set authorizationRequest(authorizationRequest: AssistantAuthorizationRequest) {
        this._authorizationRequest = authorizationRequest;
        if (!!authorizationRequest) {
            this._mapToForm();
        } else {
            this.clearForm();
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onClickRefuse(): void {
        this._refuse();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override buildForm(): void {
        this.form = this.formBuilder.group({
            client: [null, Validators.required]
        });
    }

    /**
     * Autorise l'assistant sur le dossier choisi, puis rend la main à l'assistant par
     * l'adresse de retour que renvoie le serveur. Le chargement reste affiché pendant
     * la navigation : la page n'a plus rien à proposer.
     */
    protected override save(): void {
        this.loading = true;
        this.assistantAuthorizationService.authorize$(this.authorizationRequest, this._mapFromForm()).subscribe({
            next: (result: AssistantAuthorizationResult) => {
                window.location.href = result.redirectUrl;
            },
            error: () => this.loading = false
        });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _mapFromForm(): Client {
        return this.form.get('client')!.value;
    }

    /** Un seul dossier éligible : le choix est implicite, la page le montre sans le demander. */
    private _mapToForm(): void {
        const clients = this.authorizationRequest.clients ?? [];
        this.form.patchValue({
            client: clients.length === 1 ? clients[0] : null
        });
    }

    /**
     * Refus de l'utilisateur : l'assistant est prévenu par son adresse de retour, sans
     * appel au serveur. L'adresse lue dans l'URL n'est suivie que parce que le serveur
     * l'a validée pour cet assistant en contrôlant la demande : sur une demande refusée,
     * le formulaire n'est pas affiché, et la page ne redirige nulle part.
     */
    private _refuse(): void {
        const url = new URL(this.authorizationRequest.redirectUri);
        url.searchParams.set('error', 'access_denied');
        if (this.authorizationRequest.state) {
            url.searchParams.set('state', this.authorizationRequest.state);
        }
        window.location.href = url.toString();
    }
}
